// Telemetry dashboard for admin verifiers (verifiertype === "8").
//
// Reads the five Mongo collections the Android app pushes to via
// /api/telemetry/{overview,lead,ble,uploads,health,crashes} and renders
// the same KPIs + charts the standalone Streamlit dashboard does, but
// inside the existing webdashboard, gated by JWT + admin role.
//
// Tabs:
//   • Overview — 5 KPI tiles + stacked column "events per hour"
//   • Lead     — duration histogram by from-state + trigger-source pie + recent table
//   • BLE      — event-type bar + disconnect-reason pie + conn-duration histogram + recent table
//   • Uploads  — latency scatter + p50/p95/p99 KPI tiles + failed-uploads table
//   • Health   — battery / memory / pending-ecg lines (one series per user_id) + recent table
//   • Crashes  — top exceptions bar + by-version bar + recent table + expandable stack traces
import React, { Component } from "react";
import axios from "axios";
import Highcharts from "highcharts";
import HighchartsReact from "highcharts-react-official";
import SideBar from "../../common/SideBar";
// Telemetry routes are only deployed on the `web` backend right now, so
// we pin the dashboard's API calls there via `telemetryApi` instead of
// the per-server `apiUrl`. When the routes get rolled out to web1/web2/…,
// swap this back to `apiUrl` and delete `telemetryApi` from config.js.
import { telemetryApi } from "../../../config/config";
import "./TelemetryDashboard.css";

const TAB_LABELS = ["Live", "Overview", "Lead", "BLE", "Uploads", "Health", "Crashes"];

// Small action-button style used in the Live tab rows/modal.
const btnMini = {
  padding: "3px 12px",
  fontSize: 11,
  fontWeight: 700,
  background: "rgba(90,200,250,0.12)",
  border: "1px solid #2a5a62",
  borderRadius: 6,
  color: "#5ac8fa",
  cursor: "pointer",
};

/* ───────── Small helpers ───────── */

// Build a histogram (bin counts) from a numeric array. Used by the
// Lead-duration and BLE-connection-duration charts.
function histogram(values, binCount = 30) {
  if (!values || values.length === 0) return { categories: [], counts: [] };
  const min = Math.min(...values);
  const max = Math.max(...values);
  if (min === max) return { categories: [String(min)], counts: [values.length] };
  const width = (max - min) / binCount;
  const counts = new Array(binCount).fill(0);
  for (const v of values) {
    const idx = Math.min(binCount - 1, Math.floor((v - min) / width));
    counts[idx] += 1;
  }
  const categories = counts.map((_, i) => `${Math.round(min + i * width)}`);
  return { categories, counts };
}

// "1m 23s" / "45s" / "2.3 hrs" — for human-readable durations on tables.
function fmtDurMs(ms) {
  if (ms == null || isNaN(ms)) return "—";
  if (ms < 1000) return `${Math.round(ms)} ms`;
  const s = ms / 1000;
  if (s < 60) return `${s.toFixed(1)} s`;
  const m = s / 60;
  if (m < 60) return `${m.toFixed(1)} min`;
  return `${(m / 60).toFixed(2)} hrs`;
}

function fmtTs(ms) {
  if (!ms) return "—";
  try {
    return new Date(ms).toLocaleString();
  } catch {
    return String(ms);
  }
}

// Display label for a telemetry row's owner. Backend now attaches
// `device_id` (6-digit patient id) when it can resolve the user_id
// ObjectId to a patient. Fall back to user_id otherwise.
function deviceLabel(r) {
  if (!r) return "—";
  if (r.device_id) return String(r.device_id);
  if (r.user_id) return String(r.user_id);
  return "—";
}

// Group an array of {user_id/device_id, at_ms, <metric>} into Highcharts
// series, one series per distinct device. Prefers device_id when present
// so the legend shows "245180" instead of long ObjectIds.
function groupByUser(rows, metric) {
  const byUser = {};
  for (const r of rows) {
    if (r[metric] == null || !r.at_ms) continue;
    const u = deviceLabel(r);
    if (!byUser[u]) byUser[u] = [];
    byUser[u].push([Number(r.at_ms), Number(r[metric])]);
  }
  return Object.entries(byUser).map(([name, data]) => ({
    name,
    data: data.sort((a, b) => a[0] - b[0]),
  }));
}

/* Common dark-theme chart options so every chart in the page matches. */
const DARK = {
  chart: { backgroundColor: "transparent", style: { color: "#cbd5e1" } },
  title: { style: { color: "#cbd5e1" } },
  xAxis: {
    labels: { style: { color: "#94a3b8" } },
    title: { style: { color: "#94a3b8" } },
    gridLineColor: "#334155",
    lineColor: "#334155",
  },
  yAxis: {
    labels: { style: { color: "#94a3b8" } },
    title: { style: { color: "#94a3b8" } },
    gridLineColor: "#334155",
  },
  legend: { itemStyle: { color: "#cbd5e1" } },
  credits: { enabled: false },
};

function mergeDark(opts) {
  return {
    ...DARK,
    ...opts,
    chart: { ...DARK.chart, ...(opts.chart || {}) },
    title: { ...DARK.title, ...(opts.title || {}) },
    xAxis: { ...DARK.xAxis, ...(opts.xAxis || {}) },
    yAxis: { ...DARK.yAxis, ...(opts.yAxis || {}) },
    legend: { ...DARK.legend, ...(opts.legend || {}) },
    credits: DARK.credits,
  };
}

/* ───────── Component ───────── */

class TelemetryDashboard extends Component {
  state = {
    // Filter state
    days: 7,
    userIdInput: "",
    appliedUserId: "",
    // Auto-refresh defaults ON so the Live tab stays current (30s).
    autoRefresh: true,

    // Tabs
    activeTab: "Live",

    // Per-tab payloads (null = not yet loaded)
    live: null,
    overview: null,
    lead: null,
    ble: null,
    uploads: null,
    health: null,
    crashes: null,

    // Live tab: detail modal + device commands
    liveDetail: null,
    liveCommands: null,
    cmdBusy: "",
    cmdCustom: "",
    // Live tab curation: manual add (pin) + hidden rows toggle
    addPatientInput: "",
    showHidden: false,

    loading: false,
    error: "",
  };

  refreshTimer = null;

  componentDidMount() {
    // Defence-in-depth — backend also enforces this gate. Both Admin
    // (type 1) and Admin Verifier (type 8) can see this page.
    const t = localStorage.getItem("verifiertype");
    if (t !== "8" && t !== "1") {
      this.setState({ error: "This page is for admins only." });
      return;
    }
    this.loadActiveTab();
    if (this.state.autoRefresh) {
      this.refreshTimer = setInterval(this.loadActiveTab, 30_000);
    }
  }

  componentWillUnmount() {
    if (this.refreshTimer) clearInterval(this.refreshTimer);
    if (this.cmdPollTimer) clearTimeout(this.cmdPollTimer);
  }

  toggleAutoRefresh = () => {
    this.setState(
      (s) => ({ autoRefresh: !s.autoRefresh }),
      () => {
        if (this.refreshTimer) {
          clearInterval(this.refreshTimer);
          this.refreshTimer = null;
        }
        if (this.state.autoRefresh) {
          this.refreshTimer = setInterval(this.loadActiveTab, 30_000);
        }
      }
    );
  };

  buildParams = () => {
    const { days, appliedUserId, showHidden } = this.state;
    const p = new URLSearchParams({ days: String(days) });
    if (appliedUserId) p.set("user_id", appliedUserId);
    if (showHidden) p.set("includeHidden", "1"); // Live tab only; others ignore
    return p.toString();
  };

  // Live curation: mode "hide" removes a row, "pin" force-adds a patient,
  // "clear" removes either mark. Stored server-side so every admin sees
  // the same curated list.
  setLiveVisibility = async (ids, mode) => {
    try {
      await axios.post(`${telemetryApi}api/telemetry/live/visibility`, {
        ...ids,
        mode,
        requestedBy: localStorage.getItem("verifiername") || "admin",
      });
      this.loadActiveTab();
    } catch (err) {
      const msg =
        (err.response && err.response.data && err.response.data.error) ||
        err.message;
      alert(
        "Could not update the list: " + msg +
        (err.response && err.response.status === 404
          ? " — the live/visibility route is not deployed yet."
          : "")
      );
    }
  };

  addLivePatient = () => {
    const v = (this.state.addPatientInput || "").trim();
    if (!v) return;
    this.setLiveVisibility({ device_id: v }, "pin");
    this.setState({ addPatientInput: "" });
  };

  applyFilters = () => {
    let v = this.state.userIdInput.trim();
    // A 6-digit device number is what people know — resolve it to the
    // patient's Mongo id via the loaded Live list when possible. The
    // backend also resolves digit-only values as a fallback, so this
    // works even before the Live tab has loaded.
    if (/^\d{4,10}$/.test(v) && this.state.live && Array.isArray(this.state.live.devices)) {
      const hit = this.state.live.devices.find(
        (d) => String(d.device_id) === v
      );
      if (hit && hit.user_id) v = String(hit.user_id);
    }
    this.setState({ appliedUserId: v }, this.loadActiveTab);
  };

  setTab = (tab) =>
    this.setState({ activeTab: tab }, () => {
      // Only refetch if this tab hasn't loaded yet (lazy) — Apply forces refetch.
      const key = tab.toLowerCase();
      if (this.state[key] == null) this.loadActiveTab();
    });

  loadActiveTab = async () => {
    const slug = this.state.activeTab.toLowerCase();
    this.setState({ loading: true, error: "" });
    try {
      const res = await axios.get(
        `${telemetryApi}api/telemetry/${slug}?${this.buildParams()}`
      );
      this.setState({ [slug]: res.data, loading: false });
      // An open popup's command log refreshes with the table too, so a late
      // ack (app was asleep / offline) still shows up without a click.
      if (slug === "live" && this.state.liveDetail) this.loadLiveCommands(this.state.liveDetail);
    } catch (err) {
      const msg =
        (err.response && err.response.data && err.response.data.error) ||
        err.message ||
        "Request failed";
      this.setState({ loading: false, error: msg });
    }
  };

  /* ───────── Live tab: connected-now + device commands ───────── */

  // Queue a command for the device's app to execute on its next check-in.
  // `params` (optional object) rides along in the FCM data payload —
  // e.g. { mode: "factory_reset" } for deactivate.
  sendCommand = async (d, cmd, params) => {
    const label = deviceLabel(d);
    const command = String(cmd || "").trim();
    if (!command) return;
    const key = label + ":" + command;
    this.setState({ cmdBusy: key });
    try {
      await axios.post(`${telemetryApi}api/telemetry/command`, {
        device_id: d.device_id || null,
        user_id: d.user_id || null,
        command: command,
        params: params || undefined,
        requestedBy: localStorage.getItem("verifiername") || "admin",
      });
      this.setState({ cmdBusy: "", cmdCustom: "" });
      this.cmdPollUntil = Date.now() + 60000; // watch for the ack
      if (this.state.liveDetail && deviceLabel(this.state.liveDetail) === label) {
        this.loadLiveCommands(this.state.liveDetail);
      }
      // Mode switch reflects in the badge only once the device acks + posts a
      // fresh status. Re-pull the live feed + command log shortly after so the
      // "Monitoring mode" row and ack detail update without waiting 30 s.
      if (command === "mode_remote" || command === "mode_standalone" || command === "set_mode" ||
          command === "end_monitoring") {
        setTimeout(() => {
          this.loadActiveTab();
          if (this.state.liveDetail && deviceLabel(this.state.liveDetail) === label) {
            this.loadLiveCommands(this.state.liveDetail);
          }
        }, 2000);
      }
    } catch (err) {
      this.setState({ cmdBusy: "" });
      const msg =
        (err.response && err.response.data && err.response.data.error) ||
        err.message;
      alert(
        'Command "' + command + '" failed: ' + msg +
        (err.response && err.response.status === 404
          ? " — the telemetry command routes are not deployed yet."
          : "")
      );
    }
  };

  loadLiveCommands = async (d) => {
    try {
      const p = new URLSearchParams();
      if (d.device_id) p.set("device_id", d.device_id);
      else if (d.user_id) p.set("user_id", d.user_id);
      const res = await axios.get(
        `${telemetryApi}api/telemetry/commands?` + p.toString()
      );
      const commands = (res.data && res.data.commands) || [];
      // Popup closed or switched patient while the request was in flight.
      if (!this.state.liveDetail || deviceLabel(this.state.liveDetail) !== deviceLabel(d)) return;
      this.setState({ liveCommands: commands });
      this.scheduleCommandPoll(d, commands);
    } catch (e) {
      this.setState({ liveCommands: [] });
    }
  };

  // The app acks a command a second or two AFTER /command returns, so the
  // list loaded right after sending always shows "sent". Keep re-pulling it
  // every 2 s while any recent command is still sent/pending, for up to 60 s.
  cmdPollTimer = null;
  cmdPollUntil = 0;
  scheduleCommandPoll = (d, commands) => {
    if (this.cmdPollTimer) clearTimeout(this.cmdPollTimer);
    this.cmdPollTimer = null;
    const waiting = commands.some(
      (c) => (c.status === "sent" || c.status === "pending") &&
        Date.now() - (c.requested_ms || 0) < 60000
    );
    if (!waiting || Date.now() > this.cmdPollUntil) return;
    this.cmdPollTimer = setTimeout(() => this.loadLiveCommands(d), 2000);
  };

  openLiveDetail = (d) => {
    this.cmdPollUntil = Date.now() + 60000;
    this.setState({ liveDetail: d, liveCommands: null });
    this.loadLiveCommands(d);
  };

  closeLiveDetail = () => {
    if (this.cmdPollTimer) clearTimeout(this.cmdPollTimer);
    this.cmdPollTimer = null;
    this.setState({ liveDetail: null, liveCommands: null });
  };

  // Drill into the full telemetry history of one device: apply its id as
  // the user filter and jump to the Health tab.
  viewHistoryFor = (d) => {
    const uid = String(d.user_id || d.device_id || "");
    this.setState(
      { userIdInput: uid, appliedUserId: uid, activeTab: "Health", health: null },
      this.loadActiveTab
    );
    this.closeLiveDetail();
  };

  renderLive() {
    const lv = this.state.live;
    if (!lv) {
      return (
        <p className="td-empty">
          {this.state.error
            ? "Live view unavailable (" + this.state.error + ") — the " +
              "/api/telemetry/live route is not deployed on the web backend yet."
            : "Loading…"}
        </p>
      );
    }

    const now = Date.now();
    const CONNECTED_MS = 10 * 60 * 1000;
    const IDLE_MS = 60 * 60 * 1000;
    const devices = (lv.devices || [])
      .slice()
      .sort((a, b) => (b.last_seen_ms || 0) - (a.last_seen_ms || 0));

    const statusOf = (d) => {
      const age = now - (d.last_seen_ms || 0);
      if (age <= CONNECTED_MS) return "connected";
      if (age <= IDLE_MS) return "idle";
      return "offline";
    };
    const ago = (ms) => {
      if (!ms) return "—";
      const s = Math.max(0, Math.round((now - ms) / 1000));
      if (s < 60) return s + "s ago";
      const m = Math.floor(s / 60);
      if (m < 60) return m + "m ago";
      const h = Math.floor(m / 60);
      if (h < 24) return h + "h " + (m % 60) + "m ago";
      return Math.floor(h / 24) + "d ago";
    };
    const chipStyle = (st) => ({
      padding: "2px 10px",
      borderRadius: 10,
      fontSize: 11,
      fontWeight: 700,
      whiteSpace: "nowrap",
      display: "inline-block",
      color: st === "connected" ? "#30d158" : st === "idle" ? "#ff9f0a" : "#8e8e93",
      background:
        st === "connected"
          ? "rgba(48,209,88,0.15)"
          : st === "idle"
          ? "rgba(255,159,10,0.15)"
          : "rgba(142,142,147,0.15)",
    });

    const counts = { connected: 0, idle: 0, offline: 0 };
    devices.forEach((d) => { counts[statusOf(d)]++; });

    const KPI = ({ label, value, sub }) => (
      <div className="td-kpi">
        <div className="td-kpi__label">{label}</div>
        <div className="td-kpi__value">{value}</div>
        {sub && <div className="td-kpi__sub">{sub}</div>}
      </div>
    );

    return (
      <div className="td-pane">
        <div className="td-kpi-row">
          <KPI label="Connected now" value={counts.connected} sub="event in last 10 min" />
          <KPI label="Idle" value={counts.idle} sub="seen in last hour" />
          <KPI label="Offline" value={counts.offline} sub="older than 1 hour" />
          <KPI
            label="Devices in window"
            value={devices.length}
            sub={
              lv.windowHours
                ? lv.windowHours >= 48
                  ? "last " + Math.round(lv.windowHours / 24) + " days"
                  : "last " + lv.windowHours + " hours"
                : "reporting window"
            }
          />
        </div>

        {/* List curation: manually add (pin) a patient by 6-digit id;
            toggle visibility of hidden rows. */}
        <div style={{ display: "flex", gap: 10, alignItems: "center", margin: "2px 0 12px", flexWrap: "wrap" }}>
          <input
            value={this.state.addPatientInput}
            onChange={(e) => this.setState({ addPatientInput: e.target.value })}
            onKeyDown={(e) => e.key === "Enter" && this.addLivePatient()}
            placeholder="patient id to add, e.g. 251685"
            style={{ padding: "6px 12px", background: "#0b0e11", border: "1px solid #2a2f36", borderRadius: 6, color: "#e8eaed", fontSize: 12, width: 220 }}
          />
          <button style={btnMini} onClick={this.addLivePatient}>＋ Add patient</button>
          <button style={btnMini} onClick={this.loadActiveTab} title="Fetch the latest statuses now">
            ↻ Refresh
          </button>
          <label style={{ marginLeft: "auto", display: "flex", alignItems: "center", gap: 6, fontSize: 12, color: "#8e8e93", cursor: "pointer" }}>
            <input
              type="checkbox"
              checked={this.state.showHidden}
              onChange={() =>
                this.setState({ showHidden: !this.state.showHidden }, this.loadActiveTab)
              }
            />
            show hidden
          </label>
        </div>

        <table className="td-table">
          <thead>
            <tr>
              <th>Patient</th>
              <th>Status</th>
              <th>Last seen</th>
              <th title="ECG device battery">Battery</th>
              <th title="Phone battery">Mobile</th>
              <th>Rec</th>
              <th title="Lead / wearable attached to patient (device_attached)">Lead</th>
              <th>BLE</th>
              <th>Pending ECG</th>
              <th>Last upload</th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            {devices.map((d) => {
              const st = statusOf(d);
              const label = deviceLabel(d);
              return (
                <tr key={label} style={d.hidden ? { opacity: 0.45 } : undefined}>
                  <td>
                    {d.pinned && (
                      <span title="Manually added — always listed" style={{ marginRight: 4 }}>📌</span>
                    )}
                    <button
                      onClick={() => this.openLiveDetail(d)}
                      title="Open device details + commands"
                      style={{ background: "none", border: "none", color: "#5ac8fa", cursor: "pointer", fontWeight: 700, padding: 0 }}
                    >
                      {label}
                    </button>
                    {d.serial_no && (
                      <div style={{ fontSize: 13, color: "#a8c3cc", fontFamily: "ui-monospace, monospace", fontWeight: 600, marginTop: 2 }}>
                        {d.serial_no}
                      </div>
                    )}
                  </td>
                  <td><span style={chipStyle(st)}>{st}</span></td>
                  <td>{ago(d.last_seen_ms)}</td>
                  <td>
                    {/* A stored 0% outside active recording means "wearable
                        not connected, no reading" — show a dash, not 0%. */}
                    {d.battery_pct != null &&
                    !(Number(d.battery_pct) === 0 && Number(d.recording_status) !== 1)
                      ? d.battery_pct + "%"
                      : "—"}
                  </td>
                  <td>{d.mobile_battery_pct != null ? d.mobile_battery_pct + "%" : "—"}</td>
                  <td>
                    {(() => {
                      const rs = Number(d.recording_status);
                      if (rs === 1) return <span style={{ color: "#30d158", fontWeight: 600 }}>recording</span>;
                      if (rs === 3) return <span style={{ color: "#5ac8fa", fontWeight: 600 }}>uploading</span>;
                      if (rs === 0) return <span style={{ color: "#8e8e93" }}>not recording</span>;
                      return "—";
                    })()}
                  </td>
                  <td>
                    {d.device_attached == null ? "—" : d.device_attached ? (
                      <span style={{ color: "#30d158", fontWeight: 600 }}>on</span>
                    ) : (
                      <span style={{ color: "#ff453a", fontWeight: 600 }}>off</span>
                    )}
                  </td>
                  <td>{d.ble_connected == null ? "—" : d.ble_connected ? "yes" : "no"}</td>
                  <td>{d.pending_ecg != null ? d.pending_ecg : "—"}</td>
                  <td>{ago(d.last_upload_ms)}</td>
                  <td style={{ whiteSpace: "nowrap" }}>
                    <button
                      style={btnMini}
                      disabled={this.state.cmdBusy === label + ":sync_now"}
                      onClick={() => this.sendCommand(d, "sync_now")}
                    >
                      Sync
                    </button>{" "}
                    <button
                      style={btnMini}
                      disabled={this.state.cmdBusy === label + ":poll"}
                      onClick={() => this.sendCommand(d, "poll")}
                    >
                      Poll
                    </button>{" "}
                    {d.hidden || d.pinned ? (
                      <button
                        style={{ ...btnMini, color: "#8e8e93", borderColor: "#2a2f36" }}
                        title={d.hidden ? "Show this patient again" : "Remove from manual list"}
                        onClick={() => this.setLiveVisibility({ user_id: d.user_id }, "clear")}
                      >
                        {d.hidden ? "Unhide" : "Unpin"}
                      </button>
                    ) : (
                      <button
                        style={{ ...btnMini, color: "#ff9f0a", borderColor: "#5a4a2a" }}
                        title="Hide this patient from the list (shared for all admins)"
                        onClick={() => this.setLiveVisibility({ user_id: d.user_id }, "hide")}
                      >
                        ✕
                      </button>
                    )}
                  </td>
                </tr>
              );
            })}
            {!devices.length && (
              <tr>
                <td colSpan="11" className="td-empty">
                  No devices reported telemetry in the selected window.
                </td>
              </tr>
            )}
          </tbody>
        </table>

        {this.renderLiveDetail()}
      </div>
    );
  }

  renderLiveDetail() {
    const sel = this.state.liveDetail;
    if (!sel) return null;
    // Re-resolve the row from the freshest /live payload so the popup
    // values track every auto-refresh instead of freezing at open time.
    const list = (this.state.live && this.state.live.devices) || [];
    const d =
      list.find((x) => String(x.user_id) === String(sel.user_id)) || sel;
    const label = deviceLabel(d);
    const cmds = this.state.liveCommands;

    const rowS = { display: "flex", justifyContent: "space-between", padding: "4px 0", fontSize: 13 };
    const keyS = { color: "#8e8e93" };

    return (
      <div
        onClick={this.closeLiveDetail}
        style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.6)", zIndex: 9999, display: "flex", alignItems: "center", justifyContent: "center" }}
      >
        <div
          onClick={(e) => e.stopPropagation()}
          style={{ background: "#101418", border: "1px solid #2a2f36", borderRadius: 12, padding: "20px 24px", width: 600, maxWidth: "94vw", maxHeight: "85vh", overflowY: "auto", overflowX: "hidden", color: "#e8eaed" }}
        >
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 10 }}>
            <div style={{ fontSize: 17, fontWeight: 700, color: "#5ac8fa" }}>Patient {label}</div>
            <button onClick={this.closeLiveDetail} style={{ background: "none", border: "none", color: "#8e8e93", fontSize: 18, cursor: "pointer" }}>✕</button>
          </div>

          <div style={rowS}><span style={keyS}>Device serial</span><span style={{ fontFamily: "ui-monospace, monospace", fontSize: 15, fontWeight: 700, color: "#7fd8e5" }}>{d.serial_no || "—"}</span></div>
          <div style={rowS}><span style={keyS}>Last seen</span><span>{fmtTs(d.last_seen_ms)}</span></div>
          <div style={rowS}><span style={keyS}>Battery</span><span>
            {d.battery_pct != null &&
            !(Number(d.battery_pct) === 0 && Number(d.recording_status) !== 1)
              ? d.battery_pct + "%"
              : "— (no reading — wearable not connected)"}
          </span></div>
          <div style={rowS}><span style={keyS}>Mobile battery</span><span>
            {d.mobile_battery_pct != null ? d.mobile_battery_pct + "%" : "—"}
          </span></div>
          <div style={rowS}><span style={keyS}>Lead (attached)</span><span>
            {d.device_attached == null ? "—" : d.device_attached ? "on" : "off"}
          </span></div>
          <div style={rowS}><span style={keyS}>Recording</span><span>
            {Number(d.recording_status) === 1 ? "recording"
              : Number(d.recording_status) === 3 ? "uploading"
              : Number(d.recording_status) === 0 ? "not recording" : "—"}
          </span></div>
          <div style={rowS}><span style={keyS}>Monitoring mode</span><span style={{ fontWeight: 700, color: d.app_mode === "remote" ? "#5ac8fa" : d.app_mode === "standalone" ? "#c8a94a" : "#8e8e93" }}>
            {d.app_mode === "remote" ? "Remote Monitoring" : d.app_mode === "standalone" ? "Standalone" : "—"}
          </span></div>
          <div style={rowS}><span style={keyS}>BLE connected</span><span>{d.ble_connected == null ? "—" : d.ble_connected ? "yes" : "no"}</span></div>
          <div style={rowS}><span style={keyS}>Nearby devices (last scan)</span><span>
            {d.last_scan_devices_found == null ? "—"
              : d.last_scan_devices_found + (d.last_scan_ms ? " · " + fmtTs(d.last_scan_ms) : "")}
          </span></div>
          {Array.isArray(d.last_scan_devices) && d.last_scan_devices.length > 0 && (
            <div style={{ fontSize: 12, color: "#8e8e93", margin: "-4px 0 6px", wordBreak: "break-word" }}>
              {d.last_scan_devices.map(function (x) { return (x.name || x.mac) + (x.rssi != null ? " (" + x.rssi + " dBm)" : ""); }).join(", ")}
            </div>
          )}
          <div style={rowS}><span style={keyS}>Pending ECG</span><span>{d.pending_ecg != null ? d.pending_ecg : "—"}</span></div>
          <div style={rowS}><span style={keyS}>Last upload</span><span>{fmtTs(d.last_upload_ms)}</span></div>
          {d.app_version && (
            <div style={rowS}><span style={keyS}>App version</span><span>{d.app_version}</span></div>
          )}

          {/* Monitoring mode — two-state control (mode_standalone / mode_remote).
              "Standalone" is disabled while the wearable is recording, mirroring
              the app + server guard; the hint tells the user to stop first. */}
          <div style={{ margin: "14px 0 6px" }}>
            <div style={{ fontSize: 12, fontWeight: 700, color: "#8e8e93", textTransform: "uppercase", letterSpacing: 0.5, marginBottom: 6 }}>Monitoring mode</div>
            <div style={{ display: "flex", gap: 8 }}>
              {[
                ["mode_standalone", "Standalone", "standalone"],
                ["mode_remote", "Remote Monitoring", "remote"],
              ].map(([cmd, lbl, modeVal]) => {
                const isActive = d.app_mode === modeVal;
                const recActive = Number(d.recording_status) === 1 || d.recording === true;
                const recBlocked = modeVal === "standalone" && recActive;
                return (
                  <button
                    key={cmd}
                    disabled={this.state.cmdBusy === label + ":" + cmd || recBlocked || isActive}
                    title={recBlocked ? "Stop monitoring first" : isActive ? "Already in this mode" : undefined}
                    style={{
                      ...btnMini, flex: 1,
                      ...(isActive
                        ? (modeVal === "standalone"
                            ? { background: "#c8a94a", borderColor: "#c8a94a", color: "#1a1a1a" }
                            : { background: "#5ac8fa", borderColor: "#5ac8fa", color: "#06202b" })
                        : {}),
                      // The current mode is disabled (nothing to switch to), but must not
                      // look faded like a disabled button: keep it solid and bold.
                      ...(isActive ? { opacity: 1, fontWeight: 800, cursor: "default" } : {}),
                      ...(recBlocked ? { opacity: 0.4, cursor: "not-allowed" } : {}),
                    }}
                    onClick={() => this.sendCommand(d, cmd)}
                  >
                    {isActive ? "● " + lbl : lbl}
                  </button>
                );
              })}
            </div>
            {/* A dimmed Standalone (recording lock) next to a normal Remote button
                reads as "Remote is selected" — say what the state really is. */}
            <div style={{ fontSize: 11, color: "#8e8e93", marginTop: 6 }}>
              {d.app_mode !== "remote" && d.app_mode !== "standalone"
                ? "Current mode not reported by the app yet."
                : "Current: " + (d.app_mode === "remote" ? "Remote Monitoring" : "Standalone") +
                  (d.app_mode_ms ? " (as of " + fmtTs(d.app_mode_ms) + ")" : "")}
              {(Number(d.recording_status) === 1 || d.recording === true) && d.app_mode !== "standalone"
                ? " · Standalone is locked while recording — stop recording first."
                : ""}
            </div>
          </div>

          {/* End monitoring — for a finished test where the patient forgot to stop /
              log out. Unlike "Stop rec" it works while the wearable is NOT connected:
              the app ends Remote Monitoring at once (no reconnect, no upload) and stops
              the device at its next connection; the server sets live_status 0. */}
          <div style={{ margin: "14px 0 6px" }}>
            <div style={{ fontSize: 12, fontWeight: 700, color: "#8e8e93", textTransform: "uppercase", letterSpacing: 0.5, marginBottom: 6 }}>End monitoring</div>
            <button
              style={{ ...btnMini, width: "100%", color: "#ff9a94", borderColor: "#5a2a2a", background: "rgba(255,69,58,0.08)", fontWeight: 700 }}
              disabled={this.state.cmdBusy === label + ":end_monitoring"}
              title="Ends Remote Monitoring for this patient, even if the device is not connected"
              onClick={() => {
                if (!window.confirm(
                  "End monitoring for patient " + label + "?\n\n" +
                  "Use this when the test is finished. Remote Monitoring stops now (no more uploads or " +
                  "reconnects), even if the device is not connected. The device itself is stopped the next " +
                  "time it connects. Data already uploaded is kept.")) return;
                this.sendCommand(d, "end_monitoring");
              }}
            >
              ⛔ End monitoring (works without device connection)
            </button>
            <div style={{ fontSize: 11, color: "#8e8e93", marginTop: 6 }}>
              {d.ble_connected === true
                ? "Device is connected — it is stopped right away."
                : "Device not connected — monitoring ends on the phone now; the device is stopped when it next connects."}
              {" "}Needs the updated Biocalculus app; older apps only get live status 0 from the server.
            </div>
          </div>

          {/* Control commands — sent as high-priority FCM data messages
              via the backend; the app executes and acks by cmd_id. */}
          <div style={{ display: "flex", gap: 8, margin: "14px 0 6px", flexWrap: "wrap" }}>
            {[
              ["start_recording", "▶ Start rec", false, null],
              ["stop_recording", "⏹ Stop rec", false, null],
              ["sync_now", "Sync now", false, null],
              ["connect", "Connect", false, null],
              ["disconnect", "Disconnect", false, null],
              ["poll", "Poll", false, null],
              // Destructive commands — confirmation required, red styling.
              // deactivate carries mode:factory_reset in the FCM payload.
              ["clear_memory", "Clear memory", true, null],
              ["deactivate", "Deactivate", true, { mode: "factory_reset" }],
            ].map(([cmd, lbl, danger, params]) => {
              // clear_memory and deactivate only work when the wearable
              // is connected over BLE AND not recording — keep the
              // buttons disabled otherwise so they aren't sent to bounce.
              const recActive = Number(d.recording_status) === 1;
              const bleOn = d.ble_connected === true;
              const blocked = danger && (!bleOn || recActive);
              return (
                <button
                  key={cmd}
                  style={{
                    ...(danger
                      ? { ...btnMini, color: "#ff9a94", borderColor: "#5a2a2a", background: "rgba(255,69,58,0.08)" }
                      : btnMini),
                    ...(blocked ? { opacity: 0.4, cursor: "not-allowed" } : {}),
                  }}
                  disabled={this.state.cmdBusy === label + ":" + cmd || blocked}
                  title={
                    blocked
                      ? !bleOn
                        ? "Device is not connected over BLE — send Connect first"
                        : "Recording is active — stop recording first"
                      : undefined
                  }
                  onClick={() => {
                    if (danger) {
                      const warn =
                        cmd === "deactivate"
                          ? 'Send "Deactivate (factory reset)" to patient ' + label +
                            "? IRREVERSIBLE — the device may not re-pair until an admin reactivates it."
                          : 'Send "Clear memory" to patient ' + label +
                            "? The device memory will be wiped permanently.";
                      if (!window.confirm(warn)) return;
                    }
                    this.sendCommand(d, cmd, params);
                  }}
                >
                  {lbl}
                </button>
              );
            })}
            <button style={btnMini} onClick={() => this.viewHistoryFor(d)}>View full history</button>
          </div>
          <div style={{ display: "flex", gap: 8, marginBottom: 14 }}>
            <input
              value={this.state.cmdCustom}
              onChange={(e) => this.setState({ cmdCustom: e.target.value })}
              placeholder="custom command…"
              style={{ flex: 1, padding: "5px 10px", background: "#0b0e11", border: "1px solid #2a2f36", borderRadius: 6, color: "#e8eaed", fontSize: 12 }}
            />
            <button style={btnMini} onClick={() => this.sendCommand(d, this.state.cmdCustom)}>Send</button>
          </div>

          <div style={{ fontSize: 12, fontWeight: 700, color: "#8e8e93", textTransform: "uppercase", letterSpacing: 0.5, marginBottom: 6 }}>
            Recent commands
          </div>
          {cmds == null ? (
            <div className="td-empty">Loading…</div>
          ) : !cmds.length ? (
            <div style={{ fontSize: 12, color: "#8e8e93" }}>
              No commands yet (or command routes not deployed).
            </div>
          ) : (
            <table className="td-table" style={{ fontSize: 11, width: "100%", whiteSpace: "normal" }}>
              <colgroup>
                <col style={{ width: "20%" }} />
                <col style={{ width: "13%" }} />
                <col style={{ width: "20%" }} />
                <col style={{ width: "13%" }} />
                <col style={{ width: "17%" }} />
                <col style={{ width: "17%" }} />
              </colgroup>
              <thead>
                <tr><th>Command</th><th>Status</th><th>Detail</th><th>Delivery</th><th>Requested</th><th>Done</th></tr>
              </thead>
              <tbody>
                {cmds.map((c, i) => (
                  <tr key={i}>
                    <td>{c.command}{c.mode ? " (" + c.mode + ")" : ""}</td>
                    <td
                      title={c.result ? "app reported: " + c.result : ""}
                      style={{
                        color:
                          c.status === "executed" ? "#30d158"
                          : c.status === "failed" ? "#ff453a"
                          : "#ff9f0a",
                        fontWeight: 600,
                      }}
                    >
                      {c.status}
                    </td>
                    <td style={{ color: "#8e8e93" }}>{c.detail || c.result || "—"}</td>
                    <td
                      title="pushed = FCM accepted · no_token = phone has not registered · push_failed = FCM rejected"
                      style={{ color: c.delivery === "pushed" ? "#30d158" : c.delivery === "no_token" ? "#ff9f0a" : c.delivery === "push_failed" ? "#ff453a" : "#8e8e93" }}
                    >
                      {c.delivery || "—"}
                    </td>
                    <td>{fmtTs(c.requested_ms || (c.createdAt && new Date(c.createdAt).getTime()))}</td>
                    <td>{c.executed_ms ? fmtTs(c.executed_ms) : "—"}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      </div>
    );
  }

  /* ───────── Overview tab ───────── */
  renderOverview() {
    const o = this.state.overview;
    if (!o) return <p className="td-empty">Loading…</p>;

    const KPI = ({ label, value, sub }) => (
      <div className="td-kpi">
        <div className="td-kpi__label">{label}</div>
        <div className="td-kpi__value">{value}</div>
        {sub && <div className="td-kpi__sub">{sub}</div>}
      </div>
    );

    const colorMap = {
      lead: "#4caf50",
      ble: "#2196f3",
      upload: "#ff9800",
      health: "#9e9e9e",
      crash: "#f44336",
    };

    const opts = mergeDark({
      chart: { type: "column", height: 380 },
      title: { text: "Events per hour" },
      xAxis: { type: "datetime" },
      yAxis: { title: { text: "Events" } },
      plotOptions: { column: { stacking: "normal", borderWidth: 0 } },
      tooltip: { shared: true, xDateFormat: "%Y-%m-%d %H:%M" },
      series: ["lead", "ble", "upload", "health", "crash"].map((kind) => ({
        name: kind,
        color: colorMap[kind],
        data: (o.timeline || []).map((b) => [
          new Date(b.hour).getTime(),
          b[kind] || 0,
        ]),
      })),
    });

    return (
      <div className="td-pane">
        <div className="td-kpi-row">
          <KPI label="Electrode Compliance" value={`${o.kpis.leadOnPct}%`} sub="lead-on / total time" />
          <KPI label="Recording Time" value={`${o.kpis.leadOnHrs} h`} sub="lead-on hours" />
          <KPI label="BLE Disconnects" value={o.kpis.bleDisconnects} sub="in selected range" />
          <KPI
            label="Upload Success Rate"
            value={`${o.kpis.uploadSuccessPct}%`}
            sub={`${o.kpis.uploadCount} requests`}
          />
          <KPI label="App Crashes" value={o.kpis.crashes} sub="caught exceptions" />
        </div>
        <HighchartsReact highcharts={Highcharts} options={opts} />
      </div>
    );
  }

  /* ───────── Lead tab ───────── */
  renderLead() {
    const l = this.state.lead;
    if (!l) return <p className="td-empty">Loading…</p>;

    const onHist = histogram(l.durations && l.durations.ON ? l.durations.ON : []);
    const offHist = histogram(
      l.durations && l.durations.OFF ? l.durations.OFF : []
    );

    const durOpts = mergeDark({
      chart: { type: "column", height: 320 },
      title: { text: "How long each lead state lasted (milliseconds)" },
      xAxis: { categories: onHist.categories.length ? onHist.categories : offHist.categories },
      yAxis: { title: { text: "Event count" } },
      series: [
        { name: "Was ON", color: "#4caf50", data: onHist.counts },
        { name: "Was OFF", color: "#f44336", data: offHist.counts },
      ],
    });

    const triggerEntries = Object.entries(l.triggerSourceCounts || {});
    const pieOpts = mergeDark({
      chart: { type: "pie", height: 320 },
      title: { text: "What detected the state change" },
      series: [
        {
          name: "Events",
          data: triggerEntries.map(([name, y]) => ({ name, y })),
        },
      ],
    });

    return (
      <div className="td-pane">
        <div className="td-grid-2">
          <HighchartsReact highcharts={Highcharts} options={durOpts} />
          <HighchartsReact highcharts={Highcharts} options={pieOpts} />
        </div>
        <h4 className="td-h4">Recent lead state changes</h4>
        <div className="td-table-wrap">
          <table className="td-table">
            <thead>
              <tr>
                <th>When</th>
                <th>Patient device</th>
                <th>Lead transition</th>
                <th>Duration</th>
                <th>Detected by</th>
                <th>Battery</th>
                <th>BLE signal</th>
              </tr>
            </thead>
            <tbody>
              {(l.events || []).slice(0, 200).map((e, i) => (
                <tr key={i}>
                  <td>{fmtTs(e.at_ms)}</td>
                  <td title={e.user_id || ""}>{deviceLabel(e)}</td>
                  <td>
                    <span className={e.from_state === "ON" ? "td-pill td-pill--good" : "td-pill td-pill--bad"}>
                      {e.from_state || "?"}
                    </span>
                    {" → "}
                    <span className={e.to_state === "ON" ? "td-pill td-pill--good" : "td-pill td-pill--bad"}>
                      {e.to_state || "?"}
                    </span>
                  </td>
                  <td>{fmtDurMs(e.duration_ms)}</td>
                  <td>{e.trigger_source || "—"}</td>
                  <td>{e.battery_pct != null ? `${e.battery_pct}%` : "—"}</td>
                  <td>{e.ble_rssi_dbm != null ? `${e.ble_rssi_dbm} dBm` : "—"}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    );
  }

  /* ───────── BLE tab ───────── */
  renderBle() {
    const b = this.state.ble;
    if (!b) return <p className="td-empty">Loading…</p>;

    const ev = Object.entries(b.eventCounts || {});
    const barOpts = mergeDark({
      chart: { type: "column", height: 320 },
      title: { text: "BLE event counts" },
      xAxis: { categories: ev.map(([k]) => k) },
      yAxis: { title: { text: "Count" } },
      legend: { enabled: false },
      series: [
        {
          name: "Events",
          color: "#2196f3",
          data: ev.map(([, v]) => v),
        },
      ],
    });

    const rs = Object.entries(b.disconnectReasons || {});
    const pieOpts = mergeDark({
      chart: { type: "pie", height: 320 },
      title: { text: "Disconnect reasons" },
      series: [
        { name: "Disconnects", data: rs.map(([name, y]) => ({ name, y })) },
      ],
    });

    const dursSec = (b.disconnectDurations || []).map((ms) => ms / 1000);
    const dh = histogram(dursSec, 40);
    const distOpts = mergeDark({
      chart: { type: "column", height: 280 },
      title: { text: "Connection duration before disconnect (seconds)" },
      xAxis: { categories: dh.categories },
      yAxis: { title: { text: "Count" } },
      legend: { enabled: false },
      series: [{ name: "Disconnects", color: "#fb923c", data: dh.counts }],
    });

    return (
      <div className="td-pane">
        <div className="td-grid-2">
          <HighchartsReact highcharts={Highcharts} options={barOpts} />
          <HighchartsReact highcharts={Highcharts} options={pieOpts} />
        </div>
        <HighchartsReact highcharts={Highcharts} options={distOpts} />

        <h4 className="td-h4">Recent BLE connection events</h4>
        <div className="td-table-wrap">
          <table className="td-table">
            <thead>
              <tr>
                <th>When</th>
                <th>Patient device</th>
                <th>Event type</th>
                <th>Status / reason</th>
                <th>Connected for</th>
                <th>While recording</th>
                <th>Battery</th>
                <th>BLE signal</th>
              </tr>
            </thead>
            <tbody>
              {(b.events || []).slice(0, 200).map((e, i) => (
                <tr key={i}>
                  <td>{fmtTs(e.at_ms)}</td>
                  <td title={e.user_id || ""}>{deviceLabel(e)}</td>
                  <td>
                    <span
                      className={
                        "td-pill " +
                        (e.event_type === "BLE_DISCONNECT"
                          ? "td-pill--bad"
                          : "td-pill--good")
                      }
                    >
                      {e.event_type || "?"}
                    </span>
                  </td>
                  <td>{e.status_text || "—"}</td>
                  <td>{fmtDurMs(e.connection_duration_ms)}</td>
                  <td>{e.was_during_recording ? "yes" : "no"}</td>
                  <td>{e.battery_pct != null ? `${e.battery_pct}%` : "—"}</td>
                  <td>{e.ble_rssi_dbm != null ? `${e.ble_rssi_dbm} dBm` : "—"}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    );
  }

  /* ───────── Uploads tab ───────── */
  renderUploads() {
    const u = this.state.uploads;
    if (!u) return <p className="td-empty">Loading…</p>;

    const ok = (u.series || []).filter((p) => p.success).map((p) => [p.at_ms, p.duration_ms]);
    const bad = (u.series || []).filter((p) => !p.success).map((p) => [p.at_ms, p.duration_ms]);

    const scatterOpts = mergeDark({
      chart: { type: "scatter", height: 360, zoomType: "xy" },
      title: { text: "Upload latency over time" },
      xAxis: { type: "datetime" },
      yAxis: { title: { text: "duration_ms" } },
      tooltip: { headerFormat: "", pointFormat: "{point.x:%Y-%m-%d %H:%M}<br><b>{point.y} ms</b>" },
      series: [
        { name: "Success", color: "#4caf50", marker: { symbol: "circle", radius: 3 }, data: ok },
        { name: "Failed", color: "#f44336", marker: { symbol: "triangle", radius: 4 }, data: bad },
      ],
    });

    const KPI = ({ label, value }) => (
      <div className="td-kpi">
        <div className="td-kpi__label">{label}</div>
        <div className="td-kpi__value">{value}</div>
      </div>
    );

    return (
      <div className="td-pane">
        <div className="td-kpi-row td-kpi-row--3">
          <KPI label="Median Upload (p50)"   value={u.percentiles.p50 != null ? `${u.percentiles.p50} ms` : "—"} sub="50% of uploads are faster" />
          <KPI label="Slow Upload (p95)"     value={u.percentiles.p95 != null ? `${u.percentiles.p95} ms` : "—"} sub="95% of uploads are faster" />
          <KPI label="Worst Upload (p99)"    value={u.percentiles.p99 != null ? `${u.percentiles.p99} ms` : "—"} sub="tail latency / outliers" />
        </div>
        <HighchartsReact highcharts={Highcharts} options={scatterOpts} />

        <h4 className="td-h4">Failed upload attempts</h4>
        {(u.failures || []).length === 0 ? (
          <p className="td-empty">No failures in range. 🎉</p>
        ) : (
          <div className="td-table-wrap">
            <table className="td-table">
              <thead>
                <tr>
                  <th>When</th>
                  <th>Patient device</th>
                  <th>API endpoint</th>
                  <th>HTTP code</th>
                  <th>Error message</th>
                  <th>Duration</th>
                </tr>
              </thead>
              <tbody>
                {u.failures.map((e, i) => (
                  <tr key={i}>
                    <td>{fmtTs(e.at_ms)}</td>
                    <td title={e.user_id || ""}>{deviceLabel(e)}</td>
                    <td>{e.endpoint || "—"}</td>
                    <td>{e.http_code || "—"}</td>
                    <td className="td-td-error">{e.error || "—"}</td>
                    <td>{fmtDurMs(e.duration_ms)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    );
  }

  /* ───────── Health tab ───────── */
  renderHealth() {
    const h = this.state.health;
    if (!h) return <p className="td-empty">Loading…</p>;

    const snaps = h.snapshots || [];

    const lineOpts = (title, metric, unit) =>
      mergeDark({
        chart: { type: "line", height: 280, zoomType: "x" },
        title: { text: title },
        xAxis: { type: "datetime" },
        yAxis: { title: { text: unit } },
        tooltip: { shared: true, xDateFormat: "%Y-%m-%d %H:%M" },
        series: groupByUser(snaps, metric),
      });

    return (
      <div className="td-pane">
        <HighchartsReact highcharts={Highcharts} options={lineOpts("Battery % over time", "battery_pct", "%")} />
        <div className="td-grid-2">
          <HighchartsReact highcharts={Highcharts} options={lineOpts("App memory (MB)", "app_memory_mb", "MB")} />
          <HighchartsReact highcharts={Highcharts} options={lineOpts("Pending ECG backlog", "pending_ecg_count", "count")} />
        </div>

        <h4 className="td-h4">Recent device health snapshots</h4>
        <div className="td-table-wrap">
          <table className="td-table">
            <thead>
              <tr>
                <th>When</th>
                <th>Patient device</th>
                <th>Battery</th>
                <th>Charging</th>
                <th>App memory</th>
                <th>Network</th>
                <th>Wi-Fi signal</th>
                <th>BLE connected</th>
                <th>Pending ECG</th>
                <th>Doze mode</th>
              </tr>
            </thead>
            <tbody>
              {snaps
                .slice()
                .reverse()
                .slice(0, 200)
                .map((e, i) => (
                  <tr key={i}>
                    <td>{fmtTs(e.at_ms)}</td>
                    <td title={e.user_id || ""}>{deviceLabel(e)}</td>
                    <td>{e.battery_pct != null ? `${e.battery_pct}%` : "—"}</td>
                    <td>{e.is_charging ? "yes" : "no"}</td>
                    <td>{e.app_memory_mb != null ? `${e.app_memory_mb} MB` : "—"}</td>
                    <td>{e.network_type || "—"}</td>
                    <td>{e.wifi_rssi_dbm != null ? `${e.wifi_rssi_dbm} dBm` : "—"}</td>
                    <td>{e.ble_connected ? "yes" : "no"}</td>
                    <td>{e.pending_ecg_count != null ? e.pending_ecg_count : "—"}</td>
                    <td>{e.is_doze_mode ? "yes" : "no"}</td>
                  </tr>
                ))}
            </tbody>
          </table>
        </div>
      </div>
    );
  }

  /* ───────── Crashes tab ───────── */
  renderCrashes() {
    const c = this.state.crashes;
    if (!c) return <p className="td-empty">Loading…</p>;

    if ((c.crashes || []).length === 0) {
      return (
        <div className="td-pane">
          <p className="td-empty td-empty--good">No crashes in range. 🎉</p>
        </div>
      );
    }

    const byEx = Object.entries(c.byException || {})
      .sort((a, b) => b[1] - a[1])
      .slice(0, 10);
    const exOpts = mergeDark({
      chart: { type: "bar", height: 360 },
      title: { text: "Top 10 exception types" },
      xAxis: { categories: byEx.map(([k]) => k), labels: { style: { color: "#94a3b8", fontSize: "11px" } } },
      yAxis: { title: { text: "Count" } },
      legend: { enabled: false },
      series: [{ name: "Crashes", color: "#f44336", data: byEx.map(([, v]) => v) }],
    });

    const byVer = Object.entries(c.byVersion || {}).sort((a, b) => b[1] - a[1]);
    const verOpts = mergeDark({
      chart: { type: "column", height: 320 },
      title: { text: "Crashes per app version" },
      xAxis: { categories: byVer.map(([k]) => k) },
      yAxis: { title: { text: "Count" } },
      legend: { enabled: false },
      series: [{ name: "Crashes", color: "#fb923c", data: byVer.map(([, v]) => v) }],
    });

    return (
      <div className="td-pane">
        <div className="td-grid-2">
          <HighchartsReact highcharts={Highcharts} options={exOpts} />
          <HighchartsReact highcharts={Highcharts} options={verOpts} />
        </div>

        <h4 className="td-h4">Recent crashes</h4>
        <div className="td-table-wrap">
          <table className="td-table">
            <thead>
              <tr>
                <th>When</th>
                <th>Patient device</th>
                <th>App version</th>
                <th>Exception class</th>
                <th>Error message</th>
                <th>Thread name</th>
                <th>Phone model</th>
              </tr>
            </thead>
            <tbody>
              {(c.crashes || []).slice(0, 200).map((e, i) => (
                <tr key={i}>
                  <td>{fmtTs(e.at_ms)}</td>
                  <td title={e.user_id || ""}>{deviceLabel(e)}</td>
                  <td>{e.app_version || "—"}</td>
                  <td className="td-td-mono">{e.exception_class || "—"}</td>
                  <td className="td-td-error">{e.message || "—"}</td>
                  <td>{e.thread || "—"}</td>
                  <td>{e.device_model || "—"}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        <h4 className="td-h4">Stack traces (20 most recent — click to expand)</h4>
        {(c.crashes || []).slice(0, 20).map((row, i) => (
          <details key={i} className="td-stack">
            <summary>
              {fmtTs(row.at_ms)} · {row.exception_class || "?"} · {deviceLabel(row)}
            </summary>
            <pre>{row.stack_trace || "(no stack trace)"}</pre>
          </details>
        ))}
      </div>
    );
  }

  render() {
    const { activeTab, days, userIdInput, autoRefresh, loading, error } = this.state;

    return (
      <div className="td-root">
        <SideBar
          menus={[
            { title: "Patient List",       url: "/patient-list" },
            { title: "Verifier Home",      url: "/verifier-home" },
            { title: "Analysis Dashboard", url: "/analysis-dashboard" },
            { title: "Users by Role",      url: "/users-by-role" },
            { title: "Telemetry",          url: "/telemetry" },
            { title: "Assign Patients",    url: "/assign-patients" },
            { title: "Verified",           url: "/Verification-Completed" },
            { title: "QC",                 url: "/qc_home" },
          ]}
          profileLink="verifier-profile"
        />

        <div className="td-page">
          <div className="td-header">
            <div>
              <h2>Patient device telemetry</h2>
              <span className="td-sub">
                Electrode contact · BLE connectivity · upload performance · device health · app crashes
              </span>
            </div>
          </div>

          {error && <div className="td-error">{error}</div>}

          <div className="td-filters">
            <label>
              Patient id
              <input
                type="text"
                placeholder="e.g. 245180"
                value={userIdInput}
                onChange={(e) => this.setState({ userIdInput: e.target.value })}
                onKeyDown={(e) => e.key === "Enter" && this.applyFilters()}
              />
            </label>
            <label>
              Last
              <input
                type="number"
                min="1"
                max="90"
                value={days}
                onChange={(e) => this.setState({ days: +e.target.value || 1 })}
              />
              days
            </label>
            <button className="td-btn" onClick={this.applyFilters} disabled={loading}>
              {loading ? "Loading…" : "Apply"}
            </button>
            <label className="td-check">
              <input
                type="checkbox"
                checked={autoRefresh}
                onChange={this.toggleAutoRefresh}
              />
              Auto-refresh 30 s
            </label>
          </div>

          <div className="td-tabs">
            {TAB_LABELS.map((t) => (
              <button
                key={t}
                className={"td-tab" + (activeTab === t ? " td-tab--active" : "")}
                onClick={() => this.setTab(t)}
              >
                {t}
              </button>
            ))}
          </div>

          {activeTab === "Live" && this.renderLive()}
          {activeTab === "Overview" && this.renderOverview()}
          {activeTab === "Lead" && this.renderLead()}
          {activeTab === "BLE" && this.renderBle()}
          {activeTab === "Uploads" && this.renderUploads()}
          {activeTab === "Health" && this.renderHealth()}
          {activeTab === "Crashes" && this.renderCrashes()}
        </div>
      </div>
    );
  }
}

export default TelemetryDashboard;
